import { Button, icons } from "@bruntworkwear-apps/ui";
import { quantity, rate } from "@/shared/lib";
import { ReceiptLinesView } from "./components/ReceiptLines.view";
import { TrackerRowsView } from "./components/TrackerRows.view";
import styles from "./PoBreakdown.module.css";
import type { PoBreakdownProps } from "./PoBreakdown.types";

/**
 * Both sides of one purchase order: the receipt lines that were costed, and
 * every tracker row considered when finding their rate.
 *
 * Rendered below the table rather than inside the clicked row. A single order
 * can carry a hundred receipt lines (PO 1327 does), which is unusable inside an
 * accordion nested in a scrolling table - here each side gets its own scroll and
 * the full width.
 */
export function PoBreakdown({ order, lines, onClose }: PoBreakdownProps) {
  const receiptLines = lines.filter((line) => (line.po ?? "") === order.po);
  const tracker = order.allocation?.allocations ?? [];
  const used = tracker.filter((entry) => entry.units_taken > 0).length;

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div className={styles.title}>
          <span className={styles.po}>PO {order.po}</span>
          <span className={styles.subtitle}>
            {quantity(order.total_quantity)} units over {order.line_count} receipt line
            {order.line_count === 1 ? "" : "s"}
            {order.allocation ? ` · rate ${rate(order.allocation.customs_rate)}` : ""}
          </span>
        </div>
        <Button variant="ghost" leadingIcon={<icons.X size={14} />} onClick={onClose}>
          Close
        </Button>
      </div>

      <div className={styles.body}>
        <section className={styles.section}>
          <div className={styles.sectionHead}>
            <span className="panel-title">Receipt lines · WC export</span>
            <span className={styles.sectionNote}>
              {receiptLines.length} line{receiptLines.length === 1 ? "" : "s"} costed
            </span>
          </div>
          <ReceiptLinesView lines={receiptLines} />
        </section>

        <section className={styles.section}>
          <div className={styles.sectionHead}>
            <span className="panel-title">Tracker rows · Ops master</span>
            <span className={styles.sectionNote}>
              {used} of {tracker.length} drawn from
            </span>
          </div>
          <TrackerRowsView entries={tracker} />
        </section>
      </div>
    </div>
  );
}
